import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Form, Col } from 'react-bootstrap';
import MenuItem from '@mui/material/MenuItem';
import { BiTrash, BiPlus, BiCheckCircle, BiErrorCircle } from 'react-icons/bi';
import { read, utils } from 'xlsx';

import ButtonLoading from '../../atoms/buttonLoading/view';
import ButtonAction from '../../atoms/buttonAction/view';
import Table from '../../molecules/tablas/view';
import ShowThird from '../../organisms/showThird/view';
import OptionsMenu from '../../atoms/OptionsMenu/view';
import UploadExcelButton from '../../molecules/UploadExcelButton/view';
import ExcelTemplateDownloadButton from '../../molecules/ExcelTemplateDownloadButton/view';
import DateSelector from '../../atoms/datepicker/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import SubaccountSearchDropdown from '../SubaccountSearchDropdown/view';
import ClientInfo from '../../atoms/ClientInfo/view';
import ThirdsModal from '../../organisms/thirdsModal/modal';

import { getDocumentTypes } from '../../../usecases/accounting-usecase';
import { getAllSubContableAcounts } from '../../../usecases/contable-acounts-usecase';
import { getThirdByIdentification } from '../../../usecases/thirds-usecase';
import { roleContext } from '../../../context/role/context';
import { listener, dispatchCustomEvent } from '../../../utils/dispatch';
import { dateFormat } from '../../../utils/helper';

import './style.css';

const ComprobanteForm = ({ initialData, onSave, submitTitle = "Guardar y Publicar" }) => {
    const { profile } = useContext(roleContext);
    const [formData, setFormData] = useState({
        descripcion: '',
        document_type: '',
        document: '',
        date: dateFormat(new Date()),
        lineas: []
    });
    const [isValidForm, setIsValidForm] = useState(false);
    const [confirm, setConfirm] = useState(undefined);
    const [allAccounts, setAllAccounts] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);

    const [currentLine, setCurrentLine] = useState({
        cuenta: undefined,
        debito: 0,
        credito: 0,
        descripcion: ''
    });
    const [lineThird, setLineThird] = useState(undefined);
    const tableRef = useRef(null);

    useEffect(() => {
        if (initialData) {
            setFormData({
                descripcion: initialData.descripcion || '',
                document_type: initialData.document_type || '',
                document: initialData.document || '',
                date: initialData.date || dateFormat(new Date()),
                lineas: initialData.lineas || []
            });
            if (initialData.lineas && initialData.lineas.length > 0) {
                setCurrentLine(p => ({
                    ...p,
                    descripcion: initialData.lineas[0].descripcion
                }));
            }
        }
    }, [initialData]);

    useEffect(() => {
        getAllSubContableAcounts("", "").then(res => {
            const mapped = res.map(item => {
                const paddedCode = String(item.code ?? '').padStart(2, '0');
                const baseAccount = item.base_account?.account || '';
                return {
                    ...item,
                    account: baseAccount ? `${baseAccount}${paddedCode}` : paddedCode
                };
            });
            setAllAccounts(mapped);
        });
        getDocumentTypes().then(res => setDocumentTypes(res));
        const unbindThird = listener("btn-thirds-add", (e) => setLineThird(e.detail.data));
        return () => unbindThird && unbindThird();
    }, []);

    useEffect(() => {
        if (formData.document_type && documentTypes.length > 0) {
            const aux = documentTypes.find(t => (t.key || t.id) === formData.document_type);
            if (aux) {
                // Re-sync the value so the <select> reflects it once options are loaded
                setFormData(p => ({ ...p, document_type: aux.key || aux.id }));
                if (aux.href) setConfirm(aux);
            }
        }
    }, [documentTypes]);

    useEffect(() => { validateForm(); }, [formData]);

    function validateForm() {
        if (!formData) return setIsValidForm(false);
        const { descripcion, document_type, lineas } = formData;
        if (!descripcion || !document_type || !lineas || lineas.length < 2) return setIsValidForm(false);
        const sums = lineas.reduce((acc, curr) => ({
            d: acc.d + (curr.valor_debito || 0),
            c: acc.c + (curr.valor_credito || 0)
        }), { d: 0, c: 0 });
        setIsValidForm(Math.abs(sums.d - sums.c) < 0.01 && sums.d > 0);
    }

    const totals = formData.lineas.reduce((acc, curr) => ({
        debito: acc.debito + (curr.valor_debito || 0),
        credito: acc.credito + (curr.valor_credito || 0)
    }), { debito: 0, credito: 0 });

    const difference = Math.abs(totals.debito - totals.credito);
    const isBalanced = difference < 0.01;

    const tableData = useMemo(() => formData.lineas.map(l => ({
        numero_linea: l.numero_linea,
        cuenta: l.aditional_info?.account || "N/A",
        descripcion: l.descripcion,
        valor_debito: l.valor_debito,
        valor_credito: l.valor_credito,
        third_id: l.tercero_id,
        cuenta_contable_id: l.cuenta_contable_id,
        centro_costo_id: l.centro_costo_id,
    })), [formData.lineas]);

    const tableFormats = useMemo(() => ({
        valor_debito: {
            type: "custom",
            children: (item, idx) => (
                <Form.Control
                    type="number"
                    value={item.valor_debito}
                    onChange={(e) => updateLine(idx, 'valor_debito', e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="table-input-inline text-end"
                    onClick={(e) => e.stopPropagation()}
                />
            )
        },
        valor_credito: {
            type: "custom",
            children: (item, idx) => (
                <Form.Control
                    type="number"
                    value={item.valor_credito}
                    onChange={(e) => updateLine(idx, 'valor_credito', e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="table-input-inline text-end"
                    onClick={(e) => e.stopPropagation()}
                />
            )
        },
        descripcion: {
            type: "custom",
            children: (item, idx) => (
                <Form.Control
                    type="text"
                    value={item.descripcion}
                    onChange={(e) => updateLine(idx, 'descripcion', e.target.value)}
                    className="table-input-inline"
                    onClick={(e) => e.stopPropagation()}
                />
            )
        },
        third_id: {
            type: "custom",
            children: (item, idx) => (
                <div className="d-flex align-items-center justify-content-center gap-2" style={{ width: '100%' }}>
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <ClientInfo
                            tercero={formData.lineas[idx]?.tercero_object}
                            onSelected={(t) => updateLine(idx, 'tercero', t)}
                            variant="small"
                            hidden={!formData.lineas[idx]?.aditional_info?.need_third}
                            skipModal={true}
                        />
                    </div>
                    <div style={{ width: '40px' }}>
                        <ShowThird profile={profile} data={item} />
                    </div>
                </div>
            )
        }
    }), [formData.lineas, profile]);

    const handleInput = (e) => {
        let { name, value } = e.target;
        if (name === 'debito' || name === 'credito') value = parseFloat(value || 0);
        setCurrentLine(prev => ({ ...prev, [name]: value }));
    };

    const updateLine = (idx, field, value) => {
        setFormData(prev => {
            const newLineas = [...prev.lineas];
            const line = { ...newLineas[idx] };
            if (field === 'cuenta') {
                line.cuenta_contable_id = value.id;
                line.aditional_info = value;
                line.descripcion = line.descripcion || value.description;
            } else if (field === 'tercero') {
                line.tercero_id = value?.id;
                line.tercero_object = value;
                if (value) {
                    setLineThird(value);
                }
            } else if (field === 'valor_debito' || field === 'valor_credito') {
                line[field] = parseFloat(value || 0);
            } else {
                line[field] = value;
            }
            newLineas[idx] = line;
            return { ...prev, lineas: newLineas };
        });
    };

    function addNewLine() {
        if (currentLine.cuenta && (currentLine.debito > 0 || currentLine.credito > 0)) {
            const newLine = {
                numero_linea: formData.lineas.length + 1,
                cuenta_contable_id: currentLine.cuenta.id,
                tercero_id: lineThird?.id || undefined,
                tercero_object: lineThird,
                centro_costo_id: 1,
                valor_debito: currentLine.debito,
                valor_credito: currentLine.credito,
                descripcion: currentLine.descripcion || currentLine.cuenta.description,
                aditional_info: currentLine.cuenta
            };
            const firstLineDesc = formData.lineas.length > 0
                ? formData.lineas[0].descripcion
                : (currentLine.descripcion || currentLine.cuenta.description);

            setFormData(prev => ({ ...prev, lineas: [...(prev.lineas || []), newLine] }));
            setCurrentLine({
                cuenta: undefined,
                debito: 0,
                credito: 0,
                descripcion: firstLineDesc
            });
            dispatchCustomEvent("clear-subaccount-search", {});
        }
    }

    const handleFileUpload = async (file) => {
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = utils.sheet_to_json(worksheet);
            const newLines = [];
            const skipped = [];
            let currentLineNumber = formData.lineas.length;

            for (let row of jsonData) {
                const accountCode = row['Cuenta']?.toString();
                const account = allAccounts.find(acc => acc.account === accountCode);
                if (!account) { skipped.push(accountCode); continue; }

                let thirdId = undefined;
                let thirdObj = undefined;
                const thirdRef = row['Tercero']?.toString();

                if (thirdRef) {
                    try {
                        const foundThird = await getThirdByIdentification(thirdRef);
                        if (foundThird) {
                            thirdId = foundThird.id;
                            thirdObj = foundThird;
                        }
                    } catch (err) {
                        console.error(`Error searching third ${thirdRef}:`, err);
                    }
                }

                currentLineNumber++;
                newLines.push({
                    numero_linea: currentLineNumber,
                    cuenta_contable_id: account.id,
                    tercero_id: thirdId,
                    tercero_object: thirdObj,
                    centro_costo_id: 1,
                    valor_debito: parseFloat(row['Debito'] || 0),
                    valor_credito: parseFloat(row['Credito'] || 0),
                    descripcion: row['Descripcion'] || account.description,
                    aditional_info: account
                });
            }

            if (newLines.length === 0) {
                dispatchCustomEvent('popup', { message: 'No se encontraron cuentas válidas en el archivo', type: 'error' });
                return;
            }

            setFormData(prev => ({ ...prev, lineas: [...(prev.lineas || []), ...newLines] }));

            const skippedMsg = skipped.length > 0 ? ` (${skipped.length} cuentas no encontradas)` : '';
            dispatchCustomEvent('popup', { message: `${newLines.length} líneas importadas${skippedMsg}`, type: 'success' });

        } catch (e) {
            dispatchCustomEvent('popup', { message: 'Error al leer el archivo Excel', type: 'error' });
        }
    };

    return (
        <Form id="comprobante-form" action="javascript:void(0);" style={{ height: '100%' }}>
            <ConfirmPopUp eventKey="create-comprobante" msg={"¿Publicar este comprobante?"} onAcept={() => confirm?.href && (window.location.href = confirm.href)} />

            <div className="header-section mb-1">
                <h5 className="m-0 fw-bold header-title">Nuevo Asiento Contable</h5>
                <div className="header-actions">
                    <ExcelTemplateDownloadButton />
                    <UploadExcelButton onUpload={handleFileUpload} />
                </div>
            </div>
            <div className="metadata-bar">
                <Form.Group className="mb-0">
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.descripcion}
                        onChange={(e) => setFormData(p => ({ ...p, descripcion: e.target.value }))}
                        placeholder="Concepto del asiento..."
                    />
                </Form.Group>
                <Form.Group className="mb-0">
                    <Form.Label>Tipo de Comprobante</Form.Label>
                    <Form.Select
                        value={formData.document_type}
                        onChange={(e) => {
                            const selectedKey = e.target.value;
                            if (!selectedKey) return;
                            const aux = documentTypes.find(t => (t.key || t.id) === selectedKey);
                            if (aux) {
                                setFormData(p => ({ ...p, document_type: (aux.key || aux.id) }));
                                if (aux.href) setConfirm(aux);
                            }
                        }}
                    >
                        <option value="">Selecciona un tipo...</option>
                        {documentTypes.map((t) => (
                            <option key={t.key || t.id} value={t.key || t.id}>
                                {t.text || t.name}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-0">
                    <Form.Label>Ref. Número</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.document}
                        onChange={(e) => setFormData(p => ({ ...p, document: e.target.value }))}
                        placeholder="#"
                    />
                </Form.Group>
                <Form.Group className="mb-0">
                    <DateSelector
                        label="Fecha de Entrada"
                        date={formData.date ? new Date(formData.date + 'T00:00:00') : new Date()}
                        onChange={(nv) => setFormData(p => ({ ...p, date: dateFormat(nv.toDate()) }))}
                    />
                </Form.Group>
            </div>



            <Col className='col-table-lines'>
                <div className="table-main-area">
                    <Table
                        ref={tableRef}
                        showSkeleton={false}
                        emptyMessage="No hay lineas"
                        maxHeight='none'
                        headersList={["#", "cuenta", "descripcion", "debito", "credito", "tercero"]}
                        data={tableData}
                        excludeList={["cuenta_contable_id", "centro_costo_id", "aditional_info"]}
                        formats={tableFormats}
                    >
                        <ButtonAction onClick={(l) => setFormData(p => ({ ...p, lineas: p.lineas.filter(i => i.numero_linea !== l.numero_linea) }))} title={<BiTrash />} />
                    </Table>
                </div>

                <div className='table-entry-footer'>
                    <table className="table mb-0">
                        <tbody>
                            <tr className="entry-row">
                                <td className="col-linea">{formData.lineas.length + 1}</td>
                                <td className="col-cuenta"><SubaccountSearchDropdown selected={currentLine.cuenta} onSelected={(e) => setCurrentLine(p => ({ ...p, cuenta: e }))} /></td>
                                <td className="col-desc">
                                    <Form.Control
                                        type="text"
                                        name="descripcion"
                                        placeholder="Detalle de línea..."
                                        className="table-input"
                                        value={currentLine.descripcion}
                                        onChange={handleInput}
                                    />
                                </td>
                                <td className="col-debit">
                                    <Form.Control
                                        type="number"
                                        name="debito"
                                        className="text-end table-input"
                                        value={currentLine.debito}
                                        onChange={handleInput}
                                    />
                                </td>
                                <td className="col-credit">
                                    <Form.Control
                                        type="number"
                                        name="credito"
                                        className="text-end table-input"
                                        value={currentLine.credito}
                                        onChange={handleInput}
                                    />
                                </td>
                                <td className="col-third"><ClientInfo tercero={lineThird} hidden={!currentLine.cuenta?.need_third} variant="small" skipModal={true} /></td>
                                <td className="col-actions text-center">
                                    <ButtonAction
                                        btnKey="add-ledger-line"
                                        onClick={addNewLine}
                                        disabled={!currentLine.cuenta || (!currentLine.debito && !currentLine.credito)}
                                        title={<BiPlus size={18} />}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="ledger-summary-row">
                    <div className="summary-stat">
                        <span className="summary-label">Total Débito</span>
                        <span className="summary-value">${totals.debito.toLocaleString()}</span>
                    </div>
                    <div className="summary-stat">
                        <span className="summary-label">Total Crédito</span>
                        <span className="summary-value">${totals.credito.toLocaleString()}</span>
                    </div>
                    <div className="summary-stat d-flex align-items-center gap-3">
                        <div className="text-end">
                            <span className="summary-label">Diferencia</span>
                            <span className={`summary-value ${isBalanced ? 'success' : 'error'}`}>$ {difference.toLocaleString()}</span>
                        </div>
                        <span className={`status-badge ${isBalanced ? 'status-balanced' : 'status-error'}`}>
                            {isBalanced ? <BiCheckCircle className="me-1" /> : <BiErrorCircle className="me-1" />}
                            {isBalanced ? "Balanceado" : "Fuera de Balance"}
                        </span>
                    </div>
                </div>
            </Col>

            <ButtonLoading
                id="comprobante-save"
                title={submitTitle}
                onClick={() => onSave && onSave(formData)}
                disabled={!isValidForm}
            />
            <ThirdsModal id='third-select' />
        </Form>
    );
};

export default ComprobanteForm;
